import { useMemo } from 'react'
import type { CategoryItem } from '../../../gateways/breadstack/categoryApis'
import { buildParentMap, hasAncestorIn } from '../utils/categoryLookups'

export interface CategorySearchResult {
  cat: CategoryItem
  isChild: boolean
}

interface Params {
  categories: CategoryItem[]
  /** Raw (already debounced, if desired) search text. */
  query: string
  /** Numeric category ids that must not be offered (already picked). */
  hiddenIds: Set<number>
  /**
   * Optional: categories whose descendants are already covered (excluding a
   * parent blocks its children). Descendants of these ids are hidden too.
   * Omitted by the Pickup / Delivery dialog, so its behaviour is unchanged.
   */
  coveredIds?: Set<number>
  limit?: number
}

// Hierarchical category search shared by the excluded-categories pickers.
// Each root (sorted by name) is immediately followed by its children (sorted
// by name). When searching, a parent is kept as context whenever it OR any of
// its children matches, so a matched child never appears orphaned. A matched
// parent surfaces all its children.
export function useCategorySearchResults({
  categories,
  query,
  hiddenIds,
  coveredIds,
  limit = 8,
}: Params): CategorySearchResult[] {
  const parentMap = useMemo(() => buildParentMap(categories), [categories])
  return useMemo(() => {
    const trimmed = query.trim().toLowerCase()
    const ids = new Set(categories.map((c) => c.id))
    // A row is a root if it has no parent OR its parent is missing from the
    // list (mirrors the Product list category filter).
    const isRoot = (c: CategoryItem) => !c.parentId || !ids.has(c.parentId)
    const byName = (a: CategoryItem, b: CategoryItem) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    const isAvailable = (c: CategoryItem) => {
      const id = Number(c.id)
      if (hiddenIds.has(id)) return false
      return !(coveredIds && coveredIds.size > 0 && hasAncestorIn(id, coveredIds, parentMap))
    }
    const matches = (c: CategoryItem) =>
      !trimmed || c.name.toLowerCase().includes(trimmed)

    const ordered: CategorySearchResult[] = []
    categories
      .filter(isRoot)
      .sort(byName)
      .forEach((root) => {
        const availChildren = categories
          .filter((c) => !isRoot(c) && c.parentId === root.id && isAvailable(c))
          .sort(byName)
        const rootMatches = matches(root)
        const matchedChildren = availChildren.filter(matches)
        const childrenToShow = rootMatches ? availChildren : matchedChildren
        const showRoot = rootMatches || matchedChildren.length > 0

        if (showRoot && isAvailable(root)) {
          ordered.push({ cat: root, isChild: false })
        }
        childrenToShow.forEach((child) =>
          ordered.push({ cat: child, isChild: true }),
        )
      })

    return ordered.slice(0, limit)
  }, [categories, hiddenIds, coveredIds, parentMap, query, limit])
}
